import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/supabase-auth";
import { useUserRole } from "@/hooks/useUserRole";
import { PageLoader } from "./PageLoader";
import { supabase } from "@/integrations/supabase/client";
import { buildReturnToUrl } from "@/utils/routing";
import { ACCOUNT_ROUTES } from "@/routes/routes";

interface AdminRouteProps {
  children: ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const location = useLocation();
  const { session, loading: authLoading } = useAuth();
  const { isLoading: rolesLoading, hasRole } = useUserRole();
  const [mfaChecked, setMfaChecked] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  // Check MFA status for admin users (defect #11)
  useEffect(() => {
    async function checkMfa() {
      if (!session?.user?.id) {
        setMfaEnabled(false);
        setMfaChecked(true);
        return;
      }
      
      try {
        const { data } = await supabase.rpc('user_has_mfa_enabled', {
          p_user_id: session.user.id
        });
        setMfaEnabled(!!data);
      } catch {
        // If MFA check fails, allow access but log warning
        console.warn('[AdminRoute] MFA check failed, proceeding without enforcement');
      }
      setMfaChecked(true);
    }
    
    if (session?.user?.id && !mfaChecked) {
      checkMfa();
    } else if (!session) {
      setMfaEnabled(false);
      setMfaChecked(true);
    }
  }, [session, mfaChecked]);

  // Wait for auth, roles, and MFA check to resolve
  if (authLoading || rolesLoading || !mfaChecked) {
    return <PageLoader />;
  }

  // Not authenticated
  if (!session) {
    const loginUrl = buildReturnToUrl("/login", `${location.pathname}${location.search}`);
    return <Navigate to={loginUrl} replace />;
  }

  // Allow admin role (highest privilege)
  const isAuthorized = hasRole('admin');

  if (!isAuthorized) {
    // Do NOT send to "/" — that creates a loop with Index's redirect
    return <Navigate to="/account-type-selection" replace />;
  }

  // MFA guard bypass check - redirect to MFA setup if not enabled
  // Note: This is a soft check - production should enforce stricter MFA requirements
  if (!mfaEnabled && import.meta.env.VITE_MFA_REQUIRED_FOR_ADMIN === 'true') {
    return <Navigate to={ACCOUNT_ROUTES.securityMfaSetup} replace state={{ requireMfa: true }} />;
  }

  return <>{children}</>;
}
